<template>
  <div>
    <b-row class="match-height">
      <b-col
        v-for="card in firstRowStatisticCards"
        :key="card.status"
        cols="12"
        md="6"
      >
        <rc-overlay
          :show="statsLoading"
          rounded
        >
          <statistic-card-horizontal
            :icon="card.icon"
            :statistic="card.value"
            :statistic-title="card.title"
            :color="card.color"
          />
        </rc-overlay>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        v-for="card in secondRowStatisticCards"
        :key="card.status"
        cols="12"
        md="4"
      >
        <rc-overlay
          :show="statsLoading"
          rounded
        >
          <statistic-card-horizontal
            :icon="card.icon"
            :statistic="card.value"
            :statistic-title="card.title"
            :color="card.color"
          />
        </rc-overlay>
      </b-col>
    </b-row>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-1">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-end"
          >
            <b-form-group
              :label="$t('dispatcher.order-request.review.filters.limit')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.limit"
                :options="limitOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
            class="ml-auto"
          >
            <b-form-group
              :label="$t('dispatcher.order-request.review.filters.status')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.status"
                :options="statusOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="2"
          >
            <b-form-group
              :label="$t('dispatcher.order-request.review.filters.priority')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.priority"
                :options="priorityOptions"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <rc-overlay :show="loading">
        <b-table
          :items="requests"
          :fields="tableColumns"
          responsive
          primary-key="id"
          show-empty
          :empty-text="loading ? $t('shared.loading') : $t('shared.no-data')"
        >
          <template #cell(status)="data">
            <div class="d-flex flex-column align-items-start">
              <b-badge
                class="font-medium-1"
                :variant="statusVariant(data.item.status)"
              >
                {{ statusLabel(data.item.status) }}
              </b-badge>
              <b-badge
                v-if="priorityLabel(data.item.priority) !== '-'"
                pill
                class="mt-25 font-small-1"
                :variant="priorityVariant(data.item.priority)"
              >
                {{ priorityLabel(data.item.priority) }}
              </b-badge>
            </div>
          </template>
          <template #cell(customerName)="data">
            {{ resolveCustomerName(data.item) }}
          </template>
          <template #cell(requestedType)="data">
            <b-badge
              pill
              variant="light-info"
            >
              {{ requestedTypeLabel(data.item.requestedType) }}
            </b-badge>
          </template>
          <template #cell(requestedStartAt)="data">
            <b-badge
              variant="light-secondary"
              class="text-nowrap"
            >
              {{ formatDate(data.item.requestedStartAt) }}
            </b-badge>
          </template>
          <template #cell(updatedAt)="data">
            <b-badge
              variant="light-secondary"
              class="text-nowrap"
            >
              {{ formatDate(data.item.updatedAt) }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              :to="{ name: 'dispatcher-order-request-review-details', params: { id: data.item.id } }"
            >
              {{ $t('dispatcher.order-request.review.actions.open') }}
            </b-button>
          </template>
        </b-table>
      </rc-overlay>

      <table-bottom
        :query="query"
        :total="total"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormSelect,
  BRow,
  BTable,
} from 'bootstrap-vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import PusherClient from '@/libs/pusher'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import TableBottom from '@/layouts/components/table/TableBottom.vue'
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import {
  PRIORITY_CODES,
  getPriorityLabelKey,
  getPriorityVariant,
  mapApiPriorityToCode,
  mapPriorityCodeToApiValue,
} from '@/views/external-order-request/helpers/priority'
import {
  getExternalOrderRequestStatusVariant,
  pendingReviewStatusFilterValue,
} from './helpers/reviewFormModel'

export default {
  name: 'DispatcherOrderRequestReviewList',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormSelect,
    BRow,
    BTable,
    StatisticCardHorizontal,
    RcOverlay,
    TableBottom,
  },
  data() {
    return {
      loading: false,
      statsLoading: false,
      customersLookupById: {},
      query: {
        status: pendingReviewStatusFilterValue,
        priority: null,
        limit: 25,
        page: 1,
        sortField: 'createdAt',
        sortDir: 'DESC',
      },
      limitOptions: [10, 25, 50, 100].map(limit => ({ value: limit, text: limit })),
      anyStatusValue: '__all__',
      refreshTimeoutId: null,
      pusherExternalOrderRequestHandler: null,
      pusherOrderHandler: null,
    }
  },
  computed: {
    statuses() {
      return this.$store.getters['externalOrderRequest/getExternalOrderRequestStatuses']
    },
    requests() {
      return this.$store.getters['externalOrderRequest/getDispatcherReviewRequests']
    },
    total() {
      return this.$store.getters['externalOrderRequest/getDispatcherReviewRequestsTotal']
    },
    statusTotals() {
      return this.$store.getters['externalOrderRequest/getDispatcherReviewStatusTotals']
    },
    statusOptions() {
      return [
        {
          value: pendingReviewStatusFilterValue,
          text: this.$t('dispatcher.order-request.review.filters.pending-only'),
        },
        {
          value: this.anyStatusValue,
          text: this.$t('dispatcher.order-request.review.filters.any-status'),
        },
        ...this.statuses.map(status => ({
          value: status,
          text: this.statusLabel(status),
        })),
      ]
    },
    priorityOptions() {
      return [
        {
          value: null,
          text: this.$t('dispatcher.order-request.review.filters.any-priority'),
        },
        ...PRIORITY_CODES.map(code => ({
          value: code,
          text: this.$t(getPriorityLabelKey(code)),
        })),
      ]
    },
    firstRowStatisticCards() {
      return [
        {
          status: 'submitted',
          title: this.statusLabel('submitted'),
          value: this.statusTotals.submitted || 0,
          icon: 'SendIcon',
          color: 'warning',
        },
        {
          status: 'changes_requested',
          title: this.statusLabel('changes_requested'),
          value: this.statusTotals.changes_requested || 0,
          icon: 'Edit3Icon',
          color: 'warning',
        },
      ]
    },
    secondRowStatisticCards() {
      return [
        {
          status: 'accepted',
          title: this.statusLabel('accepted'),
          value: this.statusTotals.accepted || 0,
          icon: 'CheckIcon',
          color: 'success',
        },
        {
          status: 'rejected',
          title: this.statusLabel('rejected'),
          value: this.statusTotals.rejected || 0,
          icon: 'XIcon',
          color: 'danger',
        },
        {
          status: 'cancelled',
          title: this.statusLabel('cancelled'),
          value: this.statusTotals.cancelled || 0,
          icon: 'MinusIcon',
          color: 'secondary',
        },
      ]
    },
    tableColumns() {
      return [
        { key: 'status', label: this.$t('partner.order-request.fields.status') },
        { key: 'externalPartnerName', label: this.$t('dispatcher.order-request.review.fields.partner') },
        { key: 'customerName', label: this.$t('partner.order-request.fields.customer') },
        { key: 'requestedType', label: this.$t('partner.order-request.fields.requested-type') },
        { key: 'requestedStartAt', label: this.$t('partner.order-request.fields.requested-start-at') },
        { key: 'updatedAt', label: this.$t('partner.order-request.fields.updated-at') },
        { key: 'actions', label: this.$t('shared.actions') },
      ]
    },
  },
  watch: {
    query: {
      deep: true,
      handler() {
        this.fetchData()
      },
    },
  },
  created() {
    this.initializePage()
  },
  mounted() {
    this.subscribeToPusher()
  },
  beforeDestroy() {
    this.unsubscribeFromPusher()
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId)
      this.refreshTimeoutId = null
    }
  },
  methods: {
    initializePage() {
      this.fetchCustomersLookup()
      this.refreshAll()
    },
    refreshAll() {
      this.fetchData()
      this.refreshStatusTotals()
    },
    fetchData() {
      this.loading = true
      const payload = {
        limit: this.query.limit,
        page: this.query.page,
        sortField: this.query.sortField,
        sortDir: this.query.sortDir,
      }

      if (this.query.status === this.anyStatusValue) {
        payload.status = this.statuses.join(',')
      } else if (this.query.status) {
        payload.status = this.query.status
      }

      if (this.query.priority) {
        payload.priority = mapPriorityCodeToApiValue(this.query.priority)
      }

      this.$store.dispatch('externalOrderRequest/fetchDispatcherReviewRequests', payload)
        .catch(error => {
          parseRequestError(error)
          window.toast.notify.danger(this.$t('dispatcher.order-request.review.errors.fetch-list'))
        })
        .finally(() => {
          this.loading = false
        })
    },
    refreshStatusTotals() {
      this.statsLoading = true
      const payload = {}
      if (this.query.priority) {
        payload.priority = mapPriorityCodeToApiValue(this.query.priority)
      }

      this.$store.dispatch('externalOrderRequest/fetchDispatcherReviewStatusTotals', payload)
        .catch(error => {
          parseRequestError(error)
        })
        .finally(() => {
          this.statsLoading = false
        })
    },
    fetchCustomersLookup() {
      this.$store.dispatch('customer/fetchCustomers', {
        limit: 1000,
        page: 1,
        sortField: 'name',
        sortDir: 'ASC',
      })
        .then(response => {
          const items = Array.isArray(response?.data?.items) ? response.data.items : []
          this.customersLookupById = items.reduce((acc, item) => {
            if (item?.id) {
              acc[item.id] = item.name
            }
            return acc
          }, {})
        })
        .catch(() => {
          this.customersLookupById = {}
        })
    },
    scheduleRealtimeRefresh() {
      if (this.refreshTimeoutId) {
        return
      }

      this.refreshTimeoutId = setTimeout(() => {
        this.refreshTimeoutId = null
        this.refreshAll()
      }, 350)
    },
    subscribeToPusher() {
      if (this.pusherExternalOrderRequestHandler || this.pusherOrderHandler) {
        return
      }

      this.pusherExternalOrderRequestHandler = () => this.scheduleRealtimeRefresh()
      this.pusherOrderHandler = () => this.scheduleRealtimeRefresh()

      PusherClient.bind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
      PusherClient.bind('update', 'order', this.pusherOrderHandler, this)
    },
    unsubscribeFromPusher() {
      if (this.pusherExternalOrderRequestHandler) {
        PusherClient.unbind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
        this.pusherExternalOrderRequestHandler = null
      }

      if (this.pusherOrderHandler) {
        PusherClient.unbind('update', 'order', this.pusherOrderHandler, this)
        this.pusherOrderHandler = null
      }
    },
    requestedTypeLabel(type) {
      if (!type) {
        return '-'
      }

      const i18nKey = `order.types.${type}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return type
    },
    priorityLabel(priorityValue) {
      const priorityCode = mapApiPriorityToCode(priorityValue)
      if (!priorityCode) {
        return '-'
      }

      return this.$t(getPriorityLabelKey(priorityCode))
    },
    priorityVariant(priorityValue) {
      const priorityCode = mapApiPriorityToCode(priorityValue)
      return getPriorityVariant(priorityCode)
    },
    resolveCustomerName(item) {
      if (item?.customerName) {
        return item.customerName
      }

      if (item?.customerId && this.customersLookupById[item.customerId]) {
        return this.customersLookupById[item.customerId]
      }

      return item?.customerId || '-'
    },
    statusLabel(status) {
      if (!status) {
        return '-'
      }

      const i18nKey = `partner.order-request.statuses.${status}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return status
    },
    statusVariant(status) {
      return getExternalOrderRequestStatusVariant(status)
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      return transformDatetimeToHumanReadablePretty(value)
    },
  },
}
</script>
